import { Fetcher, HTTPError } from '@this/src/util';
import React from 'react';
import { Redirect } from 'react-router-dom';
import type { Moment } from 'moment';
import moment from 'moment';
import A from '@this/shared/atoms/a';
import { styled } from '@this/constants/themes';

import SimpleLoading from '@this/shared/simple_loading/simple_loading';
import type UserJson from '@this/domain/user/user_json';
import { isIncludeHyphen } from '@this/components/shared/checker/hyphen_checker';
import SelectNationalities from '@this/shared/nationality/select_nationalities';
import _ from 'lodash';
import { Button } from '@this/shared/ui/inputs/button';
import Box from '@material-ui/core/Box';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import DateSelector from '@this/shared/date_selector/date_selector';
// マイレージ番号を入力するinputはsharedのコンポーネントを使用するため
import { Input as SharedInput } from '@this/shared/ui/inputs/input';
import { PASSWORD_PLACEHOLDER } from '@this/components/constants/password';
import { Section, LeftLabel, SubLabel, Input, Field } from './user_edit/user_edit';
import User from '../domain/user/user';
import { reportError } from '../lib/bugsnag';

interface Props {
  onUserSignedIn: () => void;
}

interface State {
  user: User | null;
  invitationToken: string;
  orientationExpenses: boolean;
  submitting: boolean;
  editErrors: string[];
  success: boolean;
}

export default class OrganizationMemberInvitationEdit extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      user: null,
      invitationToken: utils.getParam('invitation_token'),
      orientationExpenses: utils.getParam('orientation_expenses') === 'true',
      submitting: false,
      editErrors: [],
      success: false
    };
    this.handleNationalityIdChange = this.handleNationalityIdChange.bind(this);
  }

  componentDidMount() {
    this.fetchUser();
  }

  fetchUser() {
    const params = { invitation_token: this.state.invitationToken };
    Fetcher.get<UserJson>('/users/invitation/accept', params).then(res => {
      const user = new User(res);
      this.setState({ user });
    });
  }

  /* formの内容が正しければtrueを返す */
  isValidFormContent() {
    const invalidErrors = [];
    if (this.state.user && isIncludeHyphen(this.state.user.tel)) {
      invalidErrors.push('電話番号はハイフンを含めず入力してください');
    }
    if (invalidErrors.length > 0) {
      this.setState({
        editErrors: invalidErrors
      });
      return false;
    }
    return true;
  }

  handleNationalityIdChange(id: number) {
    this.state.user!.setNationalityId(id);
  }

  handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const user = this.state.user;
    if (!user || !this.isValidFormContent()) {
      return;
    }
    this.setState({
      submitting: true,
      editErrors: []
    });
    const params: any = { user: user.invitationAcceptParams() };
    params.user.invitation_token = this.state.invitationToken;
    if (this.state.orientationExpenses) {
      params.orientation_expenses = this.state.orientationExpenses;
    }

    try {
      await Fetcher.put<any>('/users/invitation', params);
      this.props.onUserSignedIn();
      this.setState({
        editErrors: [],
        success: true,
        submitting: false
      });
      location.href = '/dashboard';
    } catch (error) {
      if (error instanceof HTTPError && error.response?.data) {
        const responseError =
          error.response.data.errors ?? error.response.data.error ?? error.response.data.message;
        const editErrors = Array.isArray(responseError)
          ? responseError
          : responseError
          ? [String(responseError)]
          : [];
        this.setState({
          editErrors,
          submitting: false
        });
      } else {
        this.setState({
          editErrors: ['通信環境が不安定です。\n時間をおいて再度お試しください。'],
          submitting: false
        });
      }
    }
  };

  render() {
    try {
      const { user, success, editErrors } = this.state;
      const afterSuccessPath = this.state.orientationExpenses ? '/aiexpense' : '/dashboard';
      return (
        <div className="content-body">
          <Snackbar
            open={editErrors.length > 0}
            autoHideDuration={6000}
            onClose={() => this.setState({ editErrors: [] })}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <MuiAlert onClose={() => this.setState({ editErrors: [] })} severity="error">
              {editErrors.map((error, i) => (
                <div key={i}>{error}</div>
              ))}
            </MuiAlert>
          </Snackbar>
          {success ? (
            <div>
              <Redirect to={afterSuccessPath} />
            </div>
          ) : user ? (
            <form onSubmit={this.handleSubmit}>
              <Section>
                <LeftLabel>メール</LeftLabel>
                <Mail>{user.email}</Mail>
              </Section>
              <Section>
                <LeftLabel htmlFor="password">パスワード</LeftLabel>
                <Input
                  type="password"
                  id="password"
                  placeholder={PASSWORD_PLACEHOLDER}
                  value={user.password || ''}
                  onChange={e => user.setPassword(e.target.value)}
                />
              </Section>
              <Section>
                <LeftLabel>氏名</LeftLabel>
                <SubLabel htmlFor="lastName">氏</SubLabel>
                <Input
                  className="user-edit__input-split"
                  type="text"
                  id="lastName"
                  placeholder="例） 山田"
                  value={user.lastName || ''}
                  onChange={e => user.setLastName(e.target.value)}
                />
                <SubLabel htmlFor="firstName">名</SubLabel>
                <Input
                  className="user-edit__input-split"
                  type="text"
                  id="firstName"
                  placeholder="例） 一郎"
                  value={user.firstName || ''}
                  onChange={e => user.setFirstName(e.target.value)}
                />
              </Section>
              <Section>
                <LeftLabel>氏名ローマ字</LeftLabel>
                <SubLabel htmlFor="lastNameRoman">氏</SubLabel>
                <Input
                  className="user-edit__input-split"
                  type="text"
                  id="lastNameRoman"
                  placeholder="例） Yamada"
                  value={user.lastNameRoman}
                  onChange={e => user.setLastNameRoman(e.target.value)}
                />
                <SubLabel htmlFor="firstNameRoman">名</SubLabel>
                <Input
                  className="user-edit__input-split"
                  type="text"
                  id="firstNameRoman"
                  placeholder="例） Ichiro"
                  value={user.firstNameRoman || ''}
                  onChange={e => user.setFirstNameRoman(e.target.value)}
                />
              </Section>
              <Section>
                <LeftLabel>氏名カナ</LeftLabel>
                <SubLabel htmlFor="lastNameKana">氏</SubLabel>
                <Input
                  className="user-edit__input-split"
                  type="text"
                  id="lastNameKana"
                  placeholder="例） ヤマダ"
                  value={user.lastNameKana || ''}
                  onChange={e => user.setLastNameKana(e.target.value)}
                />
                <SubLabel htmlFor="firstNameKana">名</SubLabel>
                <Input
                  className="user-edit__input-split"
                  type="text"
                  id="firstNameKana"
                  placeholder="例） イチロウ"
                  value={user.firstNameKana || ''}
                  onChange={e => user.setFirstNameKana(e.target.value)}
                />
              </Section>
              <Section>
                <LeftLabel htmlFor="birthdayYear">生年月日</LeftLabel>
                <DateSelector
                  onDateChange={(m: Moment) => user.setBirthdayMoment(m)}
                  date={user.birthdayMoment || moment([2000])}
                />
              </Section>
              <Section>
                <LeftLabel>国籍</LeftLabel>
                <SelectNationalities
                  nationalities={user.nationalities}
                  selectedId={user.nationalityId || undefined}
                  onSelect={this.handleNationalityIdChange}
                />
              </Section>
              <Section>
                <LeftLabel htmlFor="gender">性別</LeftLabel>
                <label>
                  <input
                    type="radio"
                    value="male"
                    checked={user.gender === 'male'}
                    onChange={() => user.setGender('male')}
                  />
                  <span>男</span>
                </label>
                <label>
                  <input
                    type="radio"
                    value="female"
                    checked={user.gender === 'female'}
                    onChange={() => user.setGender('female')}
                  />
                  <span>女</span>
                </label>
              </Section>
              <Field>
                <LeftLabel htmlFor="tel">電話番号</LeftLabel>
                <Input
                  className="user-edit__input"
                  type="text"
                  id="tel"
                  placeholder="例） 09011112222"
                  value={user.tel || ''}
                  onChange={e => user.setTel(e.target.value)}
                />
                {isIncludeHyphen(user.tel) && <div className="error">ハイフンを含めず入力してください</div>}
              </Field>
              {!user.organization?.setting?.disable_show_milage_number && (
                <Section>
                  <LeftLabel>マイレージ</LeftLabel>
                  <div className="user-edit__section-mileage__items">
                    {user.mileageNumbers.map((mileageNumber, index) => (
                      <div key={index} className="user-edit__section__mileage-items__item">
                        <select
                          value={mileageNumber.carrier}
                          onChange={e => user.setMilageCarrier(index, e.target.value)}
                        >
                          {['ANA', 'JAL', 'SFJ', 'ADO', 'SNA', 'AXM', 'その他'].map(carrier => (
                            <option key={carrier}>{carrier}</option>
                          ))}
                        </select>
                        <SharedInput
                          type="text"
                          value={mileageNumber.number}
                          onChange={e => user.setMilageNumber(index, e.target.value)}
                        />
                        <a
                          className="user-edit__section__mileage-items__remove"
                          onClick={() => user.removeMileageNumber(index)}
                        >
                          <img src="/images/close_search.png" />
                        </a>
                      </div>
                    ))}
                    <div className="user-edit__section__mileage-items__add">
                      <A onClick={() => user.addMileageNumber()}>+マイレージを追加</A>
                    </div>
                  </div>
                </Section>
              )}
              {user.organization?.setting?.send_user_forwarded_email && (
                <Field>
                  <LeftLabel htmlFor="user_forwarded_email">転送アドレス</LeftLabel>
                  {_.map(user.userForwardedEmails, (e, i) => (
                    <Input
                      key={`user_forwarded_email_${i}`}
                      type="text"
                      id="user_forwarded_email"
                      value={e.email}
                      placeholder="user@example.jp"
                      onChange={e => user.setUserForwardedEmails(e.target.value, i)}
                    />
                  ))}
                </Field>
              )}
              {this.state.submitting ? (
                <SimpleLoading />
              ) : (
                <Box mt={2} ml={2} mr={2}>
                  <Button type="submit" size="large" fullWidth>
                    保存
                  </Button>
                </Box>
              )}
            </form>
          ) : (
            <SimpleLoading />
          )}
        </div>
      );
    } catch (e) {
      reportError(e);
      return null;
    }
  }
}

const Mail = styled.p`
  overflow: auto;
`;
